import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Zap, Eye, TrendingUp, Smartphone, Search, ArrowRight, Star, Bot, Shield } from "lucide-react";
import PortfolioCarousel from "@/components/PortfolioCarousel";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import WebsiteDesignTestimonials from "@/components/WebsiteDesignTestimonials";
import BeforeAfterComparison from "@/components/BeforeAfterComparison";

const WebsiteDesign = () => {
  const features = [
    {
      icon: TrendingUp,
      title: "Pipeline-Optimized",
      description: "Every element designed to convert enterprise buyers into qualified demos"
    },
    {
      icon: Smartphone,
      title: "Enterprise UX Standards",
      description: "Professional experience that builds trust with B2B decision-makers"
    },
    {
      icon: Search,
      title: "SEO for B2B Buyers",
      description: "Rank for high-intent commercial keywords your prospects search"
    },
    {
      icon: Zap,
      title: "Sub-3s Load Times",
      description: "Performance that passes enterprise security and compliance reviews"
    },
    {
      icon: Eye,
      title: "Brand Authority",
      description: "Design that positions you alongside enterprise competitors"
    },
    {
      icon: Shield,
      title: "99.999% Uptime SLA",
      description: "Enterprise-grade hosting with Cloudflare security"
    }
  ];

  const process = [
    {
      step: "1",
      title: "Strategic Discovery",
      description: "We map your buyer journey, ICP, and conversion goals"
    },
    {
      step: "2",
      title: "Design & Development",
      description: "Custom build with CRM integration and analytics setup"
    },
    {
      step: "3",
      title: "Launch & Iterate",
      description: "Deploy and optimize based on pipeline contribution data"
    }
  ];

  return (
    <>
      <EnhancedSEO 
        type="service"
        title="B2B Website Design | Enterprise Lead Generation | VGP"
        description="Custom B2B website design built for pipeline generation. Enterprise-grade performance, CRM integration, and conversion optimization for companies serious about revenue."
        keywords="B2B website design, enterprise website, lead generation website, B2B conversion optimization, SaaS website design"
        url="/website-design"
      />
      <div className="min-h-screen">
        {/* Hero Portfolio Carousel */}
        <section className="relative py-12" style={{ background: 'var(--gradient-hero)' }}>
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white mb-8">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                B2B Websites That <span className="text-secondary">Drive Pipeline</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Enterprise-grade websites built to convert B2B buyers into qualified demos.
              </p>
            </div>
          </div>
        </section>

        {/* Portfolio Showcase */}
        <section className="py-10 bg-gradient-to-b from-primary-light/20 to-background">
          <PortfolioCarousel showFullHero={true} heroMode={true} />
        </section>

        {/* Why Your Website Matters */}
        <section className="py-10 md:py-14 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Your Website is Your <span className="text-secondary">Pipeline Foundation</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                B2B buyers research 70% of their journey before talking to sales. 
                Your website needs to qualify, educate, and convert enterprise prospects.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-2 border-secondary/20 hover:border-secondary transition-all duration-300 hover:shadow-luxury">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>


        {/* Our Process */}
        <section className="py-10 md:py-14 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                  Simple, <span className="text-secondary">Proven Process</span>
                </h2>
                <p className="text-xl text-muted-foreground">
                  From concept to conversion in just a few weeks
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {process.map((item, index) => (
                  <Card key={index} className="border-2 border-secondary/20 shadow-elegant">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-10 md:py-14 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                  Investment That <span className="text-secondary">Drives Pipeline</span>
                </h2>
                <p className="text-xl text-muted-foreground mb-8">
                  Enterprise-grade build + ongoing optimization
                </p>
              </div>

              {/* One-Time Build Cost */}
              <Card className="border-2 border-secondary shadow-glow mb-12">
                <CardContent className="p-10">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-foreground mb-2">B2B Pipeline Website</h3>
                    <div className="text-5xl font-bold text-secondary mb-4">£3,000 - £8,000</div>
                    <p className="text-muted-foreground mb-2">One-time investment • Enterprise-grade foundation</p>
                    <p className="text-lg text-foreground font-medium">
                      Let's discuss the right package for your business — we're available on call
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    {[
                      "Strategic discovery and buyer journey mapping",
                      "Custom design aligned with enterprise positioning",
                      "CRM and marketing automation integration",
                      "Conversion-optimized demo request flows",
                      "Sub-3 second load times with CDN",
                      "Full analytics and attribution setup",
                      "60 days post-launch optimization",
                      "Content management training for your team"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-secondary mr-3 flex-shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button variant="premium" size="lg" className="w-full" asChild>
                    <a href="/do-you-qualify">
                      Schedule Strategic Review <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Monthly Support Plans */}
              <div className="text-center mb-8">
                <h3 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                  Ongoing <span className="text-secondary">Optimization Plans</span>
                </h3>
                <p className="text-lg text-muted-foreground">
                  Continuous improvement based on pipeline contribution data
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Standard Support */}
                <Card className="border-2 border-secondary/20 shadow-elegant hover:shadow-luxury transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary/80 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Zap className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-2xl font-bold text-foreground mb-2">Standard Support</h4>
                      <div className="text-4xl font-bold text-secondary mb-2">£99</div>
                      <p className="text-muted-foreground">per month</p>
                    </div>

                    <div className="space-y-3 mb-6">
                      {[
                        "Monthly performance monitoring",
                        "Security updates & maintenance",
                        "Content updates (up to 2 hours/month)",
                        "Bug fixes & technical support",
                        "Basic SEO maintenance",
                        "Monthly performance report",
                        "Email support (24-48hr response)"
                      ].map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-foreground text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button variant="outline" size="lg" className="w-full" asChild>
                      <a href="/do-you-qualify">
                        Add Support Plan
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Premium Support with AI & Maximum SEO */}
                <Card className="border-2 border-secondary shadow-glow relative">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-secondary text-primary px-6 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary/80 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Bot className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-2xl font-bold text-foreground mb-2">Premium + AI & SEO</h4>
                      <div className="text-4xl font-bold text-secondary mb-2">£399</div>
                      <p className="text-muted-foreground">per month</p>
                    </div>

                    <div className="space-y-3 mb-6">
                      {[
                        "Everything in Standard Support",
                        "AI chatbot integration & optimization",
                        "Maximum SEO optimization",
                        "Advanced conversion rate optimization",
                        "A/B testing & funnel optimization",
                        "Priority technical support (4hr response)",
                        "Unlimited content updates",
                        "Monthly strategy call"
                      ].map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-foreground text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button variant="premium" size="lg" className="w-full" asChild>
                      <a href="/do-you-qualify">
                        Get Premium Support
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Ultimate Marketing Package */}
                <Card className="border-2 border-primary shadow-luxury">
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary/80 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-2xl font-bold text-foreground mb-2">Ultimate Marketing</h4>
                      <div className="text-4xl font-bold text-primary mb-2">£499</div>
                      <p className="text-muted-foreground">per month</p>
                    </div>

                    <div className="space-y-3 mb-6">
                      {[
                        "Everything in Premium + AI & SEO",
                        "Weekly marketing content posts",
                        "Social media content creation",
                        "Blog posts to boost traffic",
                        "Conversion-focused copywriting",
                        "Traffic & conversion analytics",
                        "Bi-weekly strategy sessions"
                      ].map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-foreground text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button variant="default" size="lg" className="w-full bg-primary hover:bg-primary/90" asChild>
                      <a href="/do-you-qualify">
                        Maximize Traffic & Sales
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center mt-12">
                <p className="text-lg text-muted-foreground mb-6">
                  Want full demand generation with your website?
                </p>
                <Button variant="outline" size="lg" asChild>
                  <a href="/pricing">
                    View Full Pipeline Packages <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Client Testimonials */}
        <WebsiteDesignTestimonials />

        {/* Before/After Comparisons */}
        <BeforeAfterComparison />

        {/* CTA Section */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready for a Website That Drives Pipeline?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Schedule a strategic review and see how an enterprise-grade website 
                can accelerate your revenue growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="premium" size="xl" asChild>
                  <a href="/do-you-qualify">
                    Schedule Strategic Review
                  </a>
                </Button>
                <Button variant="outline" size="xl" className="bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <a href="tel:+447857450763">
                    Call: 07857 450763
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WebsiteDesign;
